import React, { useState } from "react";
import { View, Text, Image, Pressable, Linking, Modal, Alert } from "react-native";
import { format } from "date-fns";
import { Ionicons, MaterialIcons } from "@expo/vector-icons";
import * as Sharing from 'expo-sharing';
import * as Clipboard from 'expo-clipboard';
import AttachmentPreview from "./attachment-preview";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { useRouter } from "expo-router";
import { getFormattedSupabaseUrl } from "shared-utils";

interface MessageAttachment {
    id: number;
    fileUrl: string;
    fileName: string;
    fileType: string;
    fileSize: number;
}

interface ReadReceipt {
    id: number;
    readAt: Date;
    participantId: number;
    participantName?: string;
}

interface MessageBubbleProps {
    content: string;
    timestamp: Date;
    isFromCurrentUser: boolean;
    senderName?: string;
    senderAvatar?: string;
    senderType?: string;
    senderId?: number;
    attachments?: MessageAttachment[];
    isPending?: boolean;
    readReceipts?: ReadReceipt[];
    allParticipants?: Array<{
        id: number;
        name: string;
        type: string;
        userId: number | null;
        avatar?: string | null;
    }>;
    currentUserId?: number;
    isJobConversation?: boolean;
}

const detectUrls = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = urlRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push({
                type: 'text',
                content: text.substring(lastIndex, match.index)
            });
        }

        parts.push({
            type: 'url',
            content: match[0]
        });

        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
        parts.push({
            type: 'text',
            content: text.substring(lastIndex)
        });
    }

    return parts.length === 0 ? [{ type: 'text', content: text }] : parts;
};

const LinkableText = ({ content, isFromCurrentUser }: { content: string, isFromCurrentUser: boolean }) => {
    const textParts = detectUrls(content);

    const handleLinkPress = (url: string) => {
        Alert.alert(
            "Link Options",
            url,
            [
                {
                    text: "Copy Link",
                    onPress: () => {
                        Clipboard.setStringAsync(url);
                        Alert.alert("Copied!", "Link copied to clipboard");
                    }
                },
                {
                    text: "Open Link",
                    onPress: () => {
                        Linking.canOpenURL(url).then(supported => {
                            if (supported) {
                                Linking.openURL(url);
                            } else {
                                Alert.alert("Error", "Cannot open this link");
                            }
                        });
                    }
                },
                {
                    text: "Cancel",
                    style: "cancel"
                }
            ]
        );
    };

    const handleLongPress = () => {
        Clipboard.setStringAsync(content);
        Alert.alert("Copied!", "Message copied to clipboard");
    };

    return (
        <Text
            className={`${isFromCurrentUser ? 'text-white' : 'text-gray-800'}`}
            selectable={true}
            onLongPress={handleLongPress}
        >
            {textParts.map((part, index) => {
                if (part.type === 'url') {
                    return (
                        <Text
                            key={index}
                            className={`underline ${isFromCurrentUser ? 'text-blue-200' : 'text-blue-600'}`}
                            onPress={() => handleLinkPress(part.content)}
                        >
                            {part.content}
                        </Text>
                    );
                } else {
                    return part.content;
                }
            })}
        </Text>
    );
};

export function MessageBubble({
    content,
    timestamp,
    isFromCurrentUser,
    senderName,
    senderAvatar,
    senderType,
    senderId,
    attachments = [],
    isPending = false,
    readReceipts = [],
    allParticipants = [],
    currentUserId,
    isJobConversation = false
}: MessageBubbleProps) {
    const [showReadInfo, setShowReadInfo] = useState(false);
    const formattedTime = format(new Date(timestamp), "h:mm a");

    // Filter out the current user from read receipts and keep only crew members
    const filteredReadReceipts = readReceipts.filter(receipt => {
        // Get participant info for this receipt
        const participant = allParticipants.find(p => p.id === receipt.participantId);
        // Skip if this receipt belongs to the current user or if it's a staff member
        return participant?.userId !== currentUserId && participant?.type === 'crew';
    });

    // Filter participants to exclude current user and staff members
    const otherParticipants = allParticipants.filter(p =>
        p.userId !== currentUserId && p.type === 'crew'
    );

    // Get avatar for the sender
    const getSenderAvatar = () => {
        // First try to use the provided senderAvatar
        if (senderAvatar) return senderAvatar;

        // If not provided, try to find it in the participants list
        if (senderId && senderType && allParticipants.length > 0) {
            const senderParticipant = allParticipants.find(
                p => p.type === senderType && p.userId === senderId
            );
            return senderParticipant?.avatar || null;
        }

        return null;
    };

    // Get initials for avatar fallback
    const getInitials = (name?: string) => {
        if (!name) return "?";
        const words = name.trim().split(/\s+/);
        if (words.length === 1) return words[0].charAt(0).toUpperCase();
        return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
    };

    const handleOpenAttachment = (attachment: MessageAttachment) => {
        // Get the proper URL using the shared helper function
        const fileUrl = getFormattedSupabaseUrl(
            attachment.fileUrl,
            process.env.EXPO_PUBLIC_SUPABASE_URL
        );


        // Open the URL
        Linking.canOpenURL(fileUrl).then(supported => {
            if (supported) {
                Linking.openURL(fileUrl);
            } else {
                console.error('Cannot open URL:', fileUrl);
                alert(`Cannot open this attachment (${attachment.fileName})`);
            }
        }).catch(err => {
            console.error('Error opening attachment:', err);
            alert('Failed to open the attachment. Please try again.');
        });
    };

    const renderAttachments = () => {
        if (!attachments || attachments.length === 0) return null;

        return (
            <View className="mt-2">
                {attachments.map((attachment, index) => {
                    // Log the attachment URL for debugging

                    // Make sure the URL is properly formatted for Supabase storage
                    const displayUrl = getFormattedSupabaseUrl(
                        attachment.fileUrl,
                        process.env.EXPO_PUBLIC_SUPABASE_URL
                    );


                    return (
                        <Pressable
                            key={index}
                            className="flex-row items-center bg-white bg-opacity-20 rounded-md p-2 mb-1"
                            onPress={() => handleOpenAttachment(attachment)}
                        >
                            {attachment.fileType.startsWith('image/') ? (
                                <Image
                                    source={{ uri: displayUrl }}
                                    className="w-10 h-10 rounded-sm"
                                    resizeMode="cover"
                                />
                            ) : (
                                <View className="w-10 h-10 rounded-sm bg-white bg-opacity-30 items-center justify-center">
                                    <Ionicons
                                        name={
                                            attachment.fileType.includes('pdf') ? 'document-text' :
                                                attachment.fileType.includes('doc') ? 'document' :
                                                    attachment.fileType.includes('sheet') ? 'grid' :
                                                        'document-attach'
                                        }
                                        size={20}
                                        color={isFromCurrentUser ? "white" : "#333"}
                                    />
                                </View>
                            )}
                            <View className="ml-2 flex-1">
                                <Text
                                    className={`text-xs font-medium ${isFromCurrentUser ? "text-white" : "text-gray-800"}`}
                                    numberOfLines={1}
                                >
                                    {attachment.fileName}
                                </Text>
                                <Text
                                    className={`text-xs ${isFromCurrentUser ? "text-white opacity-80" : "text-gray-600"}`}
                                >
                                    {formatFileSize(attachment.fileSize)}
                                </Text>
                            </View>
                            <Ionicons
                                name="download-outline"
                                size={18}
                                color={isFromCurrentUser ? "white" : "#666"}
                            />
                        </Pressable>
                    );
                })}
            </View>
        );
    };

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return bytes + ' B';
        else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
        else return (bytes / 1048576).toFixed(1) + ' MB';
    };

    const handleReadReceiptPress = () => {
        if (isFromCurrentUser && readReceipts.length > 0) {
            setShowReadInfo(true);
        }
    };

    const handleCopyMessage = () => {
        Clipboard.setStringAsync(content);
        Alert.alert("Copied!", "Message copied to clipboard");
    };

    // Get the sender's avatar URL or use initials
    const avatarUrl = getSenderAvatar();
    const avatarInitials = getInitials(senderName);

    const router = useRouter();

    return (
        <View className={`flex flex-row px-2 py-1 my-1 ${isFromCurrentUser ? 'justify-end' : 'justify-start'}`}>
            {/* Avatar for incoming messages (WhatsApp style) */}
            {!isFromCurrentUser && (
                <View className="mt-6 mr-1">
                    <Avatar className="h-8 w-8" alt={senderName || 'User'}>
                        {avatarUrl ? (
                            <AvatarImage source={{ uri: avatarUrl }} />
                        ) : (
                            <AvatarFallback className="bg-emerald-600">
                                <Text className="text-white text-xs font-bold">{avatarInitials}</Text>
                            </AvatarFallback>
                        )}
                    </Avatar>
                </View>
            )}

            <View className={`flex ${isFromCurrentUser ? 'items-end' : 'items-start'} max-w-[80%]`}>
                {!isFromCurrentUser && senderName && (
                    <Text className="text-xs text-gray-600 mb-1 ml-2">{senderName}</Text>
                )}
                <View
                    className={`rounded-lg p-3 ${isFromCurrentUser
                        ? 'bg-primary rounded-tr-none'
                        : 'bg-white border border-gray-200 rounded-tl-none'
                        }`}
                >
                    <LinkableText
                        content={content}
                        isFromCurrentUser={isFromCurrentUser}
                    />

                    {attachments && attachments.length > 0 && (
                        <View className="mt-2 space-y-2">
                            {attachments.map((attachment) => (
                                <AttachmentPreview
                                    key={attachment.id}
                                    attachment={attachment}
                                    isFromCurrentUser={isFromCurrentUser}
                                />
                            ))}
                        </View>
                    )}

                    {isJobConversation && (
                        <Pressable
                            className="mt-2 mb-1"
                            onPress={() => router.navigate(`/notifications`)}
                        >
                            <Text
                                className={`text-sm underline ${isFromCurrentUser ? 'text-white' : 'text-blue-600'}`}
                            >
                                Accept or Decline in your job requests
                            </Text>
                        </Pressable>
                    )}

                    <View className="flex flex-row items-center justify-end mt-1 space-x-1">
                        {/* Read receipt indicator - moved to the left of the timestamp */}
                        {isFromCurrentUser && readReceipts && readReceipts.length > 0 && (
                            <Pressable onPress={handleReadReceiptPress}>
                                <MaterialIcons
                                    name="done-all"
                                    size={14}
                                    color="white"
                                    style={{ opacity: 0.9 }}
                                />
                            </Pressable>
                        )}

                        {/* Copy message button */}
                        <Pressable onPress={handleCopyMessage} className="p-1">
                            <Ionicons
                                name="copy-outline"
                                size={12}
                                color={isFromCurrentUser ? "white" : "#666"}
                                style={{ opacity: 0.7 }}
                            />
                        </Pressable>

                        <Text
                            className={`text-xs ${isFromCurrentUser ? 'text-white opacity-80' : 'text-gray-500'
                                }`}
                        >
                            {formattedTime}
                        </Text>

                        {isPending && (
                            <MaterialIcons name="access-time" size={12} color={isFromCurrentUser ? "white" : "gray"} />
                        )}
                    </View>
                </View>
            </View>

            {/* Read info modal */}
            <Modal
                visible={showReadInfo}
                transparent={true}
                animationType="fade"
                onRequestClose={() => setShowReadInfo(false)}
            >
                <Pressable
                    className="flex-1 bg-black/50 justify-center items-center"
                    onPress={() => setShowReadInfo(false)}
                >
                    <View className="bg-white rounded-lg p-4 w-4/5 max-h-3/4">
                        <View className="flex-row justify-between items-center mb-4">
                            <Text className="text-lg font-bold">Read by Crew</Text>
                            <Pressable onPress={() => setShowReadInfo(false)}>
                                <Ionicons name="close" size={24} color="#666" />
                            </Pressable>
                        </View>

                        {filteredReadReceipts.length > 0 ? (
                            <View>
                                {filteredReadReceipts.map(receipt => (
                                    <View key={receipt.id} className="flex-row items-center py-2 border-b border-gray-100">
                                        <Text className="text-base flex-1">
                                            {receipt.participantName || `Participant ${receipt.participantId}`}
                                        </Text>
                                        <Text className="text-xs text-gray-500">
                                            {format(new Date(receipt.readAt), 'MMM d, h:mm a')}
                                        </Text>
                                    </View>
                                ))}
                            </View>
                        ) : (
                            <Text className="py-2 text-gray-500">
                                No crew members have read this message yet
                            </Text>
                        )}

                        {otherParticipants.length > filteredReadReceipts.length && (
                            <View className="mt-4">
                                <Text className="font-bold mb-2">Not read by Crew</Text>
                                {otherParticipants
                                    .filter(participant => !filteredReadReceipts.some(r => r.participantId === participant.id))
                                    .map(participant => (
                                        <View key={participant.id} className="flex-row items-center py-2 border-b border-gray-100">
                                            <Text className="text-base text-gray-500">{participant.name}</Text>
                                        </View>
                                    ))
                                }
                            </View>
                        )}
                    </View>
                </Pressable>
            </Modal>
        </View>
    );
} 